import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import SectionItem from "../OrdersSectionItem";
import CustomButton from "../../../custom/custom-button/CustomButton";
import PlusIcon from "../../../shared/icons/PlusIcon";
import OrdersSectionItemInfoColumn from "./OrdersSectionItemInfoColumn";
import OrdersDatePicker from "../../../orders/date-picker-new";
import OrdersLocationInfo from "../../../orders/location-info";
import CustomSelect from "../../../custom/custom-select";
import { Order } from "../../../../services";
import { useScreenSize } from "../../../../hooks/useScreenSize";
import { useLanguage } from "../../../../context/LanguageContext";
import AddYourCity from "../../../orders/add-your-city/AddYourCity";

const AddIntermediatePoint = ({
  countries,
  setIsOpenAdd,
  points,
  setPoints,
  errors,
  pickUpDate,
  isShowDatePicker = true,
  isRailway = false,
}) => {
  const { t } = useTranslation();
  const { isMobileView } = useScreenSize();
  const { currentLanguage } = useLanguage();
  const [activeCityPointKey, setActiveCityPointKey] = useState(null);
  const [initialCityName, setInitialCityName] = useState("");
  const cityRefreshVersionRef = useRef(0);
  const pointKeysRef = useRef(new WeakMap());
  const nextPointKeyRef = useRef(0);

  const getPointKey = useCallback((point) => {
    if (!pointKeysRef.current.has(point)) {
      pointKeysRef.current.set(point, `intermediate-point-${nextPointKeyRef.current++}`);
    }
    return pointKeysRef.current.get(point);
  }, []);

  const withPointUpdates = useCallback(
    (point, updates) => {
      const updatedPoint = { ...point, ...updates };
      pointKeysRef.current.set(updatedPoint, getPointKey(point));
      return updatedPoint;
    },
    [getPointKey],
  );

  const isAddCityOpen = activeCityPointKey !== null;
  const activeCityPoint = isAddCityOpen
    ? points.find((point) => getPointKey(point) === activeCityPointKey)
    : null;

  const updatePoint = useCallback(
    (pointKey, updates, countryId) => {
      setPoints((currentPoints) => {
        const pointIndex = currentPoints.findIndex((point) => getPointKey(point) === pointKey);
        const point = currentPoints[pointIndex];
        if (!point || (countryId && point.country?.id !== countryId)) {
          return currentPoints;
        }
        return currentPoints.map((currentPoint, index) =>
          index === pointIndex
            ? withPointUpdates(point, typeof updates === "function" ? updates(point) : updates)
            : currentPoint,
        );
      });
    },
    [getPointKey, setPoints, withPointUpdates],
  );

  const addIntermediatePoint = () => {
    setPoints((prevPoints) => [
      ...prevPoints,
      {
        country: "",
        city: "",
        address: "",
        shippingDate: null,
        shippingHourFrom: 0,
        shippingHourTo: 0,
        latLng: "",
        type: 1,
        step: prevPoints.length + 2,
        isInterimPoint: false,
        recipientName: "",
        recipientPhoneNumber: "",
        recipientEmail: "",
      },
    ]);
  };

  const deleteIntermediatePoint = (index) => {
    const pointKey = getPointKey(points[index]);
    cityRefreshVersionRef.current += 1;
    setActiveCityPointKey(null);
    setInitialCityName("");
    setPoints((currentPoints) => {
      if (!currentPoints.some((point) => getPointKey(point) === pointKey)) {
        return currentPoints;
      }
      return currentPoints
        .filter((point) => getPointKey(point) !== pointKey)
        .map((point, pointIndex) =>
          point.step === pointIndex + 2 ? point : withPointUpdates(point, { step: pointIndex + 2 }),
        );
    });
  };

  const handlePointChange = (index, field, value) => {
    updatePoint(getPointKey(points[index]), { [field]: value });
  };

  const cityRequestsKey = JSON.stringify(points.map((point) => [getPointKey(point), point.country?.id || ""]));

  useEffect(() => {
    let isActive = true;
    JSON.parse(cityRequestsKey).forEach(([pointKey, countryId]) => {
      if (countryId) {
        Order.getCities(currentLanguage, countryId)
          .then((response) => {
            if (!isActive) {
              return;
            }
            const sortedCities = response.data
              .filter((city) => city.name)
              .sort((a, b) => a.name.localeCompare(b.name));
            updatePoint(
              pointKey,
              (point) => {
                const selectedCity = point.tempCityId
                  ? sortedCities.find((city) => city.id === point.tempCityId)
                  : null;
                return {
                  cities: sortedCities,
                  ...(point.tempCityId && { tempCityId: "", ...(selectedCity && { city: selectedCity }) }),
                };
              },
              countryId,
            );
          })
          .catch((error) => {
            console.log(error);
          });
      }
    });
    return () => {
      isActive = false;
      cityRefreshVersionRef.current += 1;
    };
  }, [cityRequestsKey, currentLanguage, updatePoint]);

  const getMinimumDateForPoint = (index) => {
    if (index === 0) {
      return pickUpDate;
    } else {
      return points[index - 1]?.shippingDate || pickUpDate;
    }
  };

  return (
    <>
      {points.map((point, index) => (
        <SectionItem
          key={getPointKey(point)}
          column={index}
          title={t('addIntermediatePoint.sectionTitle')}
          counter={index + 2}
          isShadow
          isDelete
          deleteIntermediatePoint={() => deleteIntermediatePoint(index)}
          addressType={point.type}
          onAddressTypeChange={(newType) => handlePointChange(index, "type", newType)}
        >
          <OrdersSectionItemInfoColumn contentGridColumn="1fr 1fr">
            <OrdersSectionItemInfoColumn title={t('addIntermediatePoint.country.label')}>
            <CustomSelect
              key={`countries-${getPointKey(point)}`}
              labelText={t('addIntermediatePoint.country.label')}
              placeholder={t('addIntermediatePoint.country.placeholder')}
              options={countries}
              defaultValue={point.country}
              onSubmit={(item) => {
                updatePoint(getPointKey(point), {
                  country: item,
                  city: "",
                  cities: [],
                  tempCityId: "",
                  address: "",
                  recipientName: "",
                  recipientPhoneNumber: "",
                  recipientEmail: "",
                });
              }}
              returnObject
              errorText={!point?.country && errors?.[`points_${index + 2}`]?.country}
            />
            </OrdersSectionItemInfoColumn>
            <OrdersSectionItemInfoColumn title={t('addIntermediatePoint.city.label')}>
            <CustomSelect
              labelText={t('addIntermediatePoint.city.label')}
              placeholder={t('addIntermediatePoint.city.placeholder')}
              options={point.cities}
              defaultValue={point.city}
              isDisabled={!point.country}
              onSubmit={(item) => {
                updatePoint(getPointKey(point), {
                  city: item,
                  tempCityId: "",
                  address: "",
                  recipientName: "",
                  recipientPhoneNumber: "",
                  recipientEmail: "",
                });
              }}
              returnObject
              errorText={!point?.city && errors?.[`points_${index + 2}`]?.city}
              showNoOptionsMessage
              onNoOptionsClick={(searchValue) => {
                cityRefreshVersionRef.current += 1;
                setInitialCityName(searchValue);
                setActiveCityPointKey(getPointKey(point));
              }}
            />
            </OrdersSectionItemInfoColumn>
          </OrdersSectionItemInfoColumn>

          {isShowDatePicker && (
            <OrdersSectionItemInfoColumn contentGridColumn={isMobileView ? "1fr" : "1fr 1.3fr"}>
              <OrdersDatePicker
                date={point.shippingDate}
                timeStart={point.shippingHourFrom}
                timeEnd={point.shippingHourTo}
                minDate={getMinimumDateForPoint(index)}
                isSender
                isEditable
                labelText={t('addIntermediatePoint.unloadingDate.label')}
                labelTimeStart={t('addIntermediatePoint.timeStart.label')}
                labelTimeEnd={t('addIntermediatePoint.timeEnd.label')}
                placeholderText={t('addIntermediatePoint.unloadingDate.placeholder')}
                setDate={(item) => handlePointChange(index, "shippingDate", item)}
                setTimeStart={(item) => handlePointChange(index, "shippingHourFrom", item)}
                setTimeEnd={(item) => handlePointChange(index, "shippingHourTo", item)}
                errorDate={!point?.shippingDate && errors?.[`points_${index + 2}`]?.shippingDate}
                errorTimeStart={!point?.shippingHourFrom && errors?.[`points_${index + 2}`]?.shippingHourFrom}
                errortimeEnd={!point?.shippingHourTo && errors?.[`points_${index + 2}`]?.shippingHourTo}
              />
            </OrdersSectionItemInfoColumn>
          )}

          <OrdersSectionItemInfoColumn>
            <OrdersLocationInfo
            isRailway={isRailway}
            labelText={t('addIntermediatePoint.recipient.label')}
            isEditable
            isIntermediatePoint
            forceOpenDropdown={!!point.address}
            forceOpenDropdownCount={point.recipientUpdate || 0}
            countries={countries}
            address={point.address}
            countrySelected={point.country}
            citySelected={point.city}
            companyName={point.recipientName}
            phoneNumber={point.recipientPhone}
            onChangeAddress={(item) => handlePointChange(index, "address", item)}
            onRecipientPhone={(item) =>
              handlePointChange(index, "recipientPhoneNumber", item)
            }
            onCoordinates={(item) => handlePointChange(index, "latLng", item)}
            onAddressId={(item) => handlePointChange(index, "addressId", item)}
            onChangeResipient={(item) =>
              handlePointChange(index, "recipientName", item)
            }
            onChangeCountry={(item) => {
              updatePoint(getPointKey(point), { country: item, city: "", cities: [], tempCityId: "" });
            }}
            onChangeCity={(city) => {
              updatePoint(getPointKey(point), { city, tempCityId: "" });
            }}
            onRecipientEmail={(item) =>
              handlePointChange(index, "recipientEmail", item)
            }
            errorText={!point?.address && errors?.[`points_${index + 2}`]?.address}
            />
          </OrdersSectionItemInfoColumn>
        </SectionItem>
      ))}

      {activeCityPoint && (
        <AddYourCity
          countries={countries}
          country={activeCityPoint.country}
          setCountry={(country) =>
            updatePoint(activeCityPointKey, {
              country,
              city: "",
              cities: [],
              tempCityId: "",
              address: "",
              recipientName: "",
              recipientPhoneNumber: "",
              recipientEmail: "",
            })
          }
          setCity={(city) => updatePoint(activeCityPointKey, { city: city || "", tempCityId: "" })}
          setAddedCityId={(city) => updatePoint(activeCityPointKey, { city, tempCityId: "" })}
          setIsСallСities={(countryId) => {
            const pointKey = activeCityPointKey;
            const refreshVersion = cityRefreshVersionRef.current;

            Order.getCities(currentLanguage, countryId)
              .then((response) =>
                response.data
                  .filter((city) => city.name)
                  .sort((a, b) => a.name.localeCompare(b.name)),
              )
              .then((cities) => {
                if (refreshVersion === cityRefreshVersionRef.current) {
                  updatePoint(pointKey, { cities }, countryId);
                }
              })
              .catch((error) => {
                console.log(error);
              });
          }}
          isOpenAdd={isAddCityOpen}
          setIsOpenAdd={(isOpen) => {
            if (!isOpen) {
              setActiveCityPointKey(null);
              setInitialCityName("");
            }
          }}
          initialCityName={initialCityName}
        />
      )}

      <SectionItem column={1} isShadow isPSmall isMrAuto>
        <CustomButton
          text={t('addIntermediatePoint.buttons.addPoint')}
          prefixStart={<PlusIcon fill="#2857FC" />}
          isUnderline
          isBlueText
          onClick={addIntermediatePoint}
        />
      </SectionItem>
    </>
  );
};

export default AddIntermediatePoint;
